import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';

import clsx from 'clsx';

import { twMerge } from '../../../utils/twMerge';
import type { BaseIcon } from '../Icons/BaseIcon';

type BadgeBaseProps = {
  Icon: typeof BaseIcon;
  variant:
    | 'purple'
    | 'green'
    | 'red'
    | 'yellow'
    | 'blue'
    | 'lightGrey'
    | 'grey'
    | 'darkGrey'
    | 'macaw';
  size?: 'md' | 'sm' | 'xs';
  shape?: 'circle' | 'hexagon';
  className?: string;
  inverse?: boolean;
};

type SpanBadgeProps = BadgeBaseProps &
  HTMLAttributes<HTMLSpanElement> & {
    as?: 'span';
  };

type ButtonBadgeProps = BadgeBaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    as: 'button';
  };

export type Props = SpanBadgeProps | ButtonBadgeProps;

type VariantMap = Record<
  Props['variant'],
  { background: string; color: string }
>;

const VARIANT_MAP: VariantMap = {
  purple: {
    background: 'bg-[var(--tw-purple-10)]',
    color: 'text-[var(--tw-purple-50)]',
  },
  green: {
    background: 'bg-[var(--tw-green-05)]',
    color: 'text-[var(--tw-green-50)]',
  },
  red: {
    background: 'bg-[var(--tw-red-05)]',
    color: 'text-[var(--tw-red-50)]',
  },
  yellow: {
    background: 'bg-[var(--tw-yellow-05)]',
    color: 'text-[var(--tw-yellow-50)]',
  },
  blue: {
    background: 'bg-[var(--tw-blue-05)]',
    color: 'text-[var(--tw-blue-50)]',
  },
  lightGrey: {
    background: 'bg-[var(--tw-grey-05)]',
    color: 'text-helper',
  },
  grey: {
    background: 'bg-[var(--tw-grey-10)]',
    color: 'text-icon-secondary',
  },
  darkGrey: {
    background: 'bg-[var(--tw-grey-90)]',
    color: 'text-icon-primary-inverse',
  },
  macaw: {
    background: 'bg-[var(--tw-brand-50)]',
    color: 'text-[var(--tw-black)]',
  },
};

const INVERSE_VARIANT_MAP: VariantMap = {
  purple: {
    background: 'bg-[var(--tw-purple-50)]',
    color: 'text-[var(--tw-white)]',
  },
  green: {
    background: 'bg-[var(--tw-green-50)]',
    color: 'text-[var(--tw-white)]',
  },
  red: {
    background: 'bg-[var(--tw-red-50)]',
    color: 'text-[var(--tw-white)]',
  },
  yellow: {
    background: 'bg-[var(--tw-yellow-50)]',
    color: 'text-[var(--tw-white)]',
  },
  blue: {
    background: 'bg-[var(--tw-blue-50)]',
    color: 'text-[var(--tw-white)]',
  },
  lightGrey: {
    background: 'bg-[var(--tw-grey-10)]',
    color: 'text-icon-secondary',
  },
  grey: {
    background: 'bg-icon-secondary',
    color: 'text-[var(--tw-white)]',
  },
  darkGrey: {
    background: 'bg-[var(--tw-grey-90)]',
    color: 'text-icon-secondary-inverse',
  },
  macaw: VARIANT_MAP.macaw,
};

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  md: 'w-sm4 h-sm4 min-w-sm4 min-h-sm4', // 32px
  sm: 'w-sm3 h-sm3 min-w-sm3 min-h-sm3', // 24px
  xs: 'w-sm2 h-sm2 min-w-sm2 min-h-sm2', // 16px
};

const SHAPE_CLASSES: Record<NonNullable<Props['shape']>, string> = {
  circle: 'rounded-full',
  hexagon: 'rounded-none',
};

const Badge = ({
  Icon,
  variant,
  size = 'md',
  shape = 'circle',
  className,
  inverse,
  as: Element = 'span',
  ...props
}: Props) => {
  const variantMap = inverse ? INVERSE_VARIANT_MAP : VARIANT_MAP;
  const wrapperClass = clsx(
    'inline-flex items-center justify-center',
    SIZE_CLASSES[size],
  );

  if (shape === 'hexagon') {
    // Extract the CSS custom property from the background class
    const getBackgroundColor = (backgroundClass: string) =>
      backgroundClass.replace(/^bg-/, 'text-');

    const getHexagonPath = (size: NonNullable<Props['size']>) => {
      // Base path from Figma (32px)
      const basePath =
        'M14.5 0.866025C15.4282 0.330127 16.5718 0.330127 17.5 0.866025L28.3564 7.13397C29.2846 7.66987 29.8564 8.66025 29.8564 9.73205V22.2679C29.8564 23.3397 29.2846 24.3301 28.3564 24.866L17.5 31.134C16.5718 31.6699 15.4282 31.6699 14.5 31.134L3.64359 24.866C2.71539 24.3301 2.14359 23.3397 2.14359 22.268V9.73205C2.14359 8.66025 2.71539 7.66987 3.64359 7.13397L14.5 0.866025Z';

      // Scale factor for different sizes)
      const scaleFactor = {
        md: 1, // 32px - no scaling
        sm: 0.75, // 24px - 24/32 = 0.75
        xs: 0.5, // 16px - 16/32 = 0.5
      }[size];

      // Scale all coordinates in the path
      return basePath.replace(/(\d+\.?\d*)/g, match => {
        return (parseFloat(match) * scaleFactor).toString();
      });
    };

    // Get the appropriate viewBox for each size
    const getViewBox = (size: NonNullable<Props['size']>) => {
      const sizeMap = {
        md: '0 0 32 32',
        sm: '0 0 24 24',
        xs: '0 0 16 16',
      };
      return sizeMap[size];
    };

    return (
      <Element
        className={twMerge(clsx(wrapperClass, 'relative', className))}
        {...(props as Record<string, unknown>)}
      >
        <svg
          className={clsx(
            'absolute inset-0 w-full h-full',
            getBackgroundColor(variantMap[variant].background),
          )}
          fill="none"
          viewBox={getViewBox(size)}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={getHexagonPath(size)} fill="currentColor" />
        </svg>
        <Icon
          className={clsx('relative !inline-flex', variantMap[variant].color)}
          size={size}
        />
      </Element>
    );
  }

  // Circle shape
  return (
    <Element
      className={twMerge(
        clsx(
          wrapperClass,
          SHAPE_CLASSES[shape],
          variantMap[variant].background,
          variantMap[variant].color,
          className,
        ),
      )}
      {...(props as Record<string, unknown>)}
    >
      <Icon size={size} />
    </Element>
  );
};

export default Badge;
